import paginationTemplate from '@naturehouse/design-system/components/molecules/pagination/Pagination.html.njk';
import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import AbstractSubject from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import calculatePagination from '../../../../util/pagination';
import ReviewListItems from './ReviewListItems';
import type { TranslationType } from '../../../../common/TranslationManager';
import TranslationManager from '../../../../common/TranslationManager';

export default class ReviewPaginationRenderer extends AbstractSubject implements Observer {
    readonly #tm: TranslationManager;

    readonly #translations: TranslationType = {};

    get translations(): TranslationType {
        return this.#translations;
    }

    static #instancePromise: Promise<ReviewPaginationRenderer> | null = null;

    static #instance: ReviewPaginationRenderer | null = null;

    public static async getInstance(): Promise<ReviewPaginationRenderer> {
        if (ReviewPaginationRenderer.#instance) {
            return ReviewPaginationRenderer.#instance;
        }

        if (!ReviewPaginationRenderer.#instancePromise) {
            ReviewPaginationRenderer.#instancePromise =
                (async (): Promise<ReviewPaginationRenderer> => {
                    const translationManager = await TranslationManager.getInstance();
                    const instance = new ReviewPaginationRenderer(
                        ReviewListItems.getInstance(),
                        document.querySelector('[data-role="reviews-container"]') as HTMLElement,
                        translationManager
                    );
                    ReviewPaginationRenderer.#instance = instance;
                    return instance;
                })();
        }
        return ReviewPaginationRenderer.#instancePromise;
    }

    public static destroyInstance(): void {
        ReviewPaginationRenderer.#instance = null;
        ReviewPaginationRenderer.#instancePromise = null;
    }

    readonly #reviewListItems: ReviewListItems;

    readonly #parentElement: HTMLElement | null = null;

    readonly #paginationElement: HTMLElement = document.createElement('nav');

    get paginationElement(): HTMLElement {
        return this.#paginationElement;
    }

    public constructor(
        reviewListItems: ReviewListItems,
        parentElement: HTMLElement | null,
        translationManager: TranslationManager
    ) {
        super();
        this.#tm = translationManager;

        this.#paginationElement.classList.add('nh-reviews__pagination');

        this.#translations = {
            of: this.#tm.translate('van'),
            previous: this.#tm.translate('previous.action'),
            next: this.#tm.translate('next.action')
        };

        this.#reviewListItems = reviewListItems;
        this.#parentElement = parentElement;

        this.#reviewListItems.attach(this);

        if (!this.#parentElement) {
            return;
        }

        this.#parentElement.appendChild(this.#paginationElement);
    }

    public update(subject: Subject): void {
        if (!(subject instanceof ReviewListItems) || !this.#parentElement) {
            return;
        }

        this.#paginationElement.innerHTML = this.#createTemplate().innerHTML;
        this.notify();
    }

    #createTemplate(): HTMLElement {
        const displaySkip =
            (this.#reviewListItems.currentPage - 1) * this.#reviewListItems.pageSize;
        const displayLimit = this.#reviewListItems.pageSize;

        const pagination = calculatePagination(
            displaySkip,
            displayLimit,
            this.#reviewListItems.total,
            (skip: number, limit: number) => {
                const page = Math.floor(skip / limit) + 1;
                return `#${page}`;
            },
            false
        );

        const pages = Array.from(pagination.pages as Map<string, string>).map((page) => ({
            label: page[0],
            href: page[1]
        }));

        const html = paginationTemplate.render({
            labels: {
                of: this.#translations.of
            },
            current: pagination.current,
            total: pagination.total,
            pages: pages,
            previous: {
                label: this.#translations.previous,
                href: pagination.prev
            },
            next: {
                label: this.#translations.next,
                href: pagination.next
            },
            first: {
                label: '1',
                href: pagination.first
            },
            last: {
                label: pagination.total,
                href: pagination.last
            },
            data: {
                'data-role': this.#paginationElement?.dataset.role,
                'data-translations': this.#paginationElement?.dataset.translations
            }
        });

        return parseStringAsHtml(html, 'nav');
    }
}
